import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { CreateTenantModal } from './components/CreateTenantModal';
import { TenantsTable } from './components/TenantsTable';
import { useTenantsPageController } from './useTenantsPageController';
import classes from './tenants-page.module.scss';

// Architectural summary:
// Super-admin tenants management screen (Hebrew, RTL).
// Touches:
//   - tenants.service.ts (create / updateActive / deactivationImpact)
//   - AdminEventsProvider (live `tenants` snapshot — page does NOT open its own SSE)
//   - AuthenticatedRoutes /tenants (gated by SuperAdminOnly)
//   - tenants-page.module.scss (theme tokens only)
//   - Mantine MantineProvider in GlobalProvider must wrap this tree
// Breaking change risk: changing column ids/labels affects screenshots
// and the associated component styles; renaming class keys breaks the generated declarations.
export const TenantsPage: React.FC = () => {
  const {
    tenants,
    isLoading,
    companyName,
    isCreating,
    updatingTenantIds,
    isCreateOpen,
    expandedTenantId,
    displayedError,
    tenantsCountLabel,
    setCompanyName,
    setIsCreateOpen,
    handleCreateTenant,
    toggleTenantActive,
    openCreateModal,
    onToggleExpanded,
    getTenantOwnerEmailDraft,
    setTenantOwnerEmailDraft,
    getTenantIsTestDraft,
    setTenantIsTestDraft,
    getTenantRestigoBranchDraft,
    setTenantRestigoBranchDraft,
    getTenantRestigoKeyDraft,
    setTenantRestigoKeyDraft,
    getTenantOntopoKeyDraft,
    setTenantOntopoKeyDraft,
    getStagedPhotoFile,
    getTenantPhotoPreviewUrl,
    showRemovePhotoControl,
    hasTenantPanelChanges,
    stageTenantPhoto,
    stageRemoveTenantPhoto,
    saveTenantPanel,
  } = useTenantsPageController();

  return (
    <div className={classes.container}>
      <div className={classes.card}>
        <div className={classes.toolbar}>
          <div className={classes.titleBlock}>
            <h2 className={classes.title}>ניהול חברות</h2>
            <p className={classes.subtitle}>{tenantsCountLabel}</p>
          </div>
          <button
            type="button"
            className={classes.createButton}
            onClick={openCreateModal}
            data-testid="tenants-create"
          >
            <span className={classes.createLabel}>צור חברה</span>
            <FontAwesomeIcon icon={faPlus as IconProp} className={classes.plusIcon} aria-hidden="true" />
          </button>
        </div>

        {displayedError && <p className={classes.error}>{displayedError}</p>}
        <TenantsTable
          tenants={tenants}
          isLoading={isLoading}
          updatingTenantIds={updatingTenantIds}
          expandedTenantId={expandedTenantId}
          onToggleExpanded={onToggleExpanded}
          onToggleTenantActive={(tenant) => {
            void toggleTenantActive(tenant);
          }}
          getTenantOwnerEmailDraft={getTenantOwnerEmailDraft}
          setTenantOwnerEmailDraft={setTenantOwnerEmailDraft}
          getTenantIsTestDraft={getTenantIsTestDraft}
          setTenantIsTestDraft={setTenantIsTestDraft}
          getTenantRestigoBranchDraft={getTenantRestigoBranchDraft}
          setTenantRestigoBranchDraft={setTenantRestigoBranchDraft}
          getTenantRestigoKeyDraft={getTenantRestigoKeyDraft}
          setTenantRestigoKeyDraft={setTenantRestigoKeyDraft}
          getTenantOntopoKeyDraft={getTenantOntopoKeyDraft}
          setTenantOntopoKeyDraft={setTenantOntopoKeyDraft}
          getStagedPhotoFile={getStagedPhotoFile}
          getTenantPhotoPreviewUrl={getTenantPhotoPreviewUrl}
          showRemovePhotoControl={showRemovePhotoControl}
          hasTenantPanelChanges={hasTenantPanelChanges}
          onStageTenantPhoto={stageTenantPhoto}
          onStageRemoveTenantPhoto={stageRemoveTenantPhoto}
          onSaveTenantPanel={(tenant) => {
            void saveTenantPanel(tenant);
          }}
        />
      </div>

      <CreateTenantModal
        isOpen={isCreateOpen}
        companyName={companyName}
        isCreating={isCreating}
        onClose={() => setIsCreateOpen(false)}
        onSubmit={handleCreateTenant}
        onCompanyNameChange={setCompanyName}
      />
    </div>
  );
};
